import { Component, ElementRef, Inject, OnInit, ViewChild } from '@angular/core';
import { Testbed } from '../../../model/testbed';
import { MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { TestbedsService } from '../../../services/testbeds.service';
import { HttpEventType } from '@angular/common/http';
import { StorageFile } from '../../../model/storageFile';
import { Constants } from '../../../utils/constants';
import { from, Observable, of } from "rxjs";
import { catchError, map, switchMap, tap } from "rxjs/operators";
import { SnackService } from "../../../services/snack.service";
import { BytesPipe } from "../../../pipes/bytes.pipe";

export class UploadFile {
  public file!: File;
  public uploading!: boolean;
  public progress!: number;
  public error!: boolean;
  public completed!: boolean;
  public oversize!: boolean;
  public replace!: boolean;
  public nameForbidden!: boolean;
  public renaming!: boolean;
  public oldFilename!: string;
  public filename!: string;
}

@Component({
  selector: 'aw-file-upload-dialog',
  templateUrl: './file-upload-dialog.component.html',
  styleUrls: ['./file-upload-dialog.component.scss']
})
export class FileUploadDialogComponent implements OnInit {

  public fileList: UploadFile[] = [];
  private testbed$ = new Observable<Testbed>();

  @ViewChild('fileInput', { static: true }) fileInput?: ElementRef;
  sizeLimit = (Constants.general.fileSizeLimitInMegaBytes * 1024 * 1024);
  storageSpaceLeftB = 0;
  storageSpaceLeftMB = 0;
  totalSpaceB = 0;
  sumOfFilesSize = 0;
  filesizeSumTooLarge = false;
  filenames: string[] = [];
  oldFilename = '';
  isRenaming = false;
  showRenamingError = false;


  constructor(@Inject(MAT_DIALOG_DATA) public data: { testbed: Testbed, files: StorageFile[] },
    private testbedService: TestbedsService,
    private dialogService: MatDialog,
    private snackService: SnackService,
    private bytesPipe: BytesPipe
  ) {
  }

  ngOnInit(): void {

    this.testbed$ =
      of(true)
        .pipe(
          switchMap(() => {
            return this.testbedService.getTestbedById(this.data.testbed?.id);
          }),
          tap(x => this.data.testbed = x)
        );

    if (this.data.testbed.storage?.totalSpace != undefined && this.data.testbed.storage?.usage != undefined) {

      this.totalSpaceB = this.data.testbed.storage?.totalSpace * 1024 * 1024;
      this.storageSpaceLeftMB = this.convertBytesToMegaBytes((this.totalSpaceB) - this.data.testbed.storage?.usage);
      this.storageSpaceLeftB = this.storageSpaceLeftMB * 1024 * 1024;
    }
  }

  fileSelected(): void {
    if (this.fileInput?.nativeElement?.files) {
      this.addFiles(this.fileInput.nativeElement.files);
    }
  }


  fileDropped(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    if (event.dataTransfer?.files) {
      this.addFiles(event.dataTransfer.files);
    }
  }


  uploadFiles(): void {

    for (const item of this.fileList) {

      if (!item.oversize && !item.error && !item.completed) {
        item.uploading = true;
        item.progress = 0;

        let $obs = new Observable<any>();
        $obs = from(item.file.arrayBuffer())
          .pipe(
            switchMap((arrayBuffer) => {
              return this.testbedService.getSignedUrlForUpload(item.filename, arrayBuffer.byteLength, this.data.testbed.id)
                .pipe(
                  switchMap(response => {
                    return this.testbedService.uploadFile(arrayBuffer, item.filename, item.file.type, response.data.url, response.data.fields)
                      .pipe(
                        map(value => {
                          if (value.type === HttpEventType.UploadProgress) {
                            item.progress = (value.loaded / value.total) * 100;
                          } else if (value.type === HttpEventType.Response) {
                            item.progress = 100;
                            item.replace = false;
                            item.completed = true;
                            this.storageSpaceLeftB -= item.file.size;
                            this.storageSpaceLeftMB = this.convertBytesToMegaBytes(this.storageSpaceLeftB);
                            this.sumOfFilesSize = 0;
                            item.uploading = false;
                          }
                        },
                          catchError((err) => {
                            this.dialogService.closeAll();
                            item.progress = 0;
                            item.uploading = false;
                            item.error = true;
                            this.snackService.error(err);
                            return err;
                          })));
                  }),
                  catchError(err => {
                    this.dialogService.closeAll();
                    this.snackService.error(`${err.message}, operation aborted...`);
                    return err;
                  })
                );
            }
            ));
        $obs.subscribe();
      }
    }
  }


  isUploading(): boolean {
    if (!this.fileList || this.fileList.length == 0) {
      return false;
    }
    return this.fileList.filter(value => value.uploading).length !== 0;
  }

  removeFile(file: UploadFile): void {

    for (let i = 0; i < this.fileList.length; i++) {
      if (this.fileList[i].file.name == file.file.name) {
        this.sumOfFilesSize -= this.fileList[i].file.size;
        this.fileList.splice(i, 1);
        this.filenames.splice(this.filenames.indexOf(file.filename), 1);

        this.filesizeSumTooLarge = this.sumOfFilesSize > this.storageSpaceLeftB;
        return;
      }
    }
  }

  renameFile(item: UploadFile): void {
    const newFilename = item.filename;
    const found = this.filenames.find(name => name === newFilename);


    if (found && newFilename != item.oldFilename) {
      this.showRenamingError = true;
      item.renaming = true;
      item.nameForbidden = true;
      return;
    }

    item.replace = this.data.files.find(f => f.name == newFilename) != undefined;
    this.filenames.splice(this.filenames.indexOf(item.oldFilename), 1);
    this.filenames.push(newFilename);
    item.filename = newFilename;
    item.renaming = false;
    item.nameForbidden = false;
    this.isRenaming = false;
    this.showRenamingError = false;

  }

  allCompleted(): boolean {
    return this.fileList.find(value => !value.completed) == undefined;
  }

  anyCompleted(): boolean {
    const item = this.fileList.find(value => value.completed);
    if (item) {
      this.sumOfFilesSize -= item.file.size;
    }
    return item != undefined;
  }

  clearCompleted(): void {
    for (let i = this.fileList.length - 1; i >= 0; i--) {
      if (this.fileList[i].completed) {
        this.filenames.splice(this.filenames.indexOf(this.fileList[i].filename), 1);
        this.fileList.splice(i, 1);
      }
    }
    this.sumOfFilesSize = 0;
    this.filesizeSumTooLarge = false;
  }

  clearAll(): void {
    this.fileList = [];
    this.filenames = [];
    this.filesizeSumTooLarge = false;
    this.sumOfFilesSize = 0;
  }

  private addFiles(files: FileList) {

    for (let i = 0; i < files.length; i++) {
      if (this.filenames.find(filename => filename === files[i].name))
        return;


      const nf: UploadFile = {
        progress: 0,
        uploading: false,
        file: files[i],
        error: false,
        oversize: files[i].size > this.sizeLimit || this.filesizeSumTooLarge,
        completed: false,
        replace: false,
        nameForbidden: false,
        renaming: false,
        filename: files[i].name,
        oldFilename: files[i].name
      };

      nf.replace = this.data.files.find(f => f.name == nf.filename) != undefined;

      this.filenames.push(files[i].name);
      this.sumOfFilesSize += files[i].size;
      this.filesizeSumTooLarge = this.sumOfFilesSize > this.storageSpaceLeftB;
      this.fileList.push(nf);
    }
  }

  convertBytesToMegaBytes(bytes: number): number {
    return this.bytesPipe.transformToNumber(bytes);
  }

}
